/* ============================================================
   Predictive Search Overlay — JS
   Standalone web component: <predictive-search-overlay>
   Lazy initialization: only bindTriggers runs on page load.
   Everything else defers to first show() call.
   ============================================================ */

class PredictiveSearchOverlay extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this.abortController = null;
    this.fetchController = null;
    this.triggerElement = null;
    this.translations = {};
    this.cachedResults = new Map();
    this.RECENT_KEY = 'predictive-search-recent';
    this.MAX_RECENT = 5;
    this.MIN_CHARS = 2;
    this.DEBOUNCE_MS = 300;
  }

  connectedCallback() {
    // 1. Bind search trigger buttons (must run on page load)
    this.bindTriggers();

    // 2. Toggle blinker visibility (must apply immediately on render)
    const showBlinker = this.dataset.showBlinker !== 'false';
    document.querySelectorAll('.pso-blinker').forEach((el) => {
      if (!showBlinker) el.classList.add('pso-blinker--hidden');
    });

    // 3. Shopify design mode (needed for theme editor)
    if (Shopify && Shopify.designMode) {
      const section = this.closest('.shopify-section');
      if (section) {
        section.addEventListener('shopify:section:select', () => this.show());
        section.addEventListener('shopify:section:deselect', () => this.hide());
      }
    }
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
  }

  /* ---- Lazy initialization (runs once on first show) ---- */
  _initOverlay() {
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // Parse translations
    const translationsEl = this.querySelector('[data-pso-translations]');
    if (translationsEl) {
      try { this.translations = JSON.parse(translationsEl.textContent); } catch (e) { /* noop */ }
    }

    // Cache DOM refs
    this.backdrop = this.querySelector('.pso__backdrop');
    this.container = this.querySelector('.pso__container');
    this.input = this.querySelector('.pso__input');
    this.closeBtn = this.querySelector('.pso__close');

    this.recentDefault = this.querySelector('[data-pso-recent]');
    this.recentResults = this.querySelector('[data-pso-recent-results]');

    this.collectionsSection = this.querySelector('[data-pso-collections]');
    this.collectionsList = this.querySelector('[data-pso-collections-list]');
    this.pagesSection = this.querySelector('[data-pso-pages]');
    this.pagesList = this.querySelector('[data-pso-pages-list]');
    this.productsSection = this.querySelector('[data-pso-products]');
    this.productsLabel = this.querySelector('[data-pso-products-label]');
    this.productsRow = this.querySelector('[data-pso-products-row]');
    this.viewAllLink = this.querySelector('[data-pso-view-all]');

    this.suggestionEl = this.querySelector('[data-pso-suggestion]');
    this.suggestionLink = this.querySelector('[data-pso-suggestion-link]');
    this.noResultsHeading = this.querySelector('[data-pso-no-results-heading]');

    // Settings
    this.maxResults = parseInt(this.dataset.maxResults, 10) || 8;
    this.showRecentInResults = this.dataset.showRecentInResults === 'true';
    this.searchUrl = this.dataset.searchUrl || '/search/suggest';
    this.shopUrl = this.dataset.shopUrl || '';
    // Close handlers
    this.backdrop.addEventListener('click', () => this.hide(), { signal });
    this.container.addEventListener('click', (e) => e.stopPropagation(), { signal });
    this.closeBtn.addEventListener('click', () => this.hide(), { signal });
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.open) this.hide();
    }, { signal });

    // Input handlers
    this.debouncedSearch = this.debounce(this.onInput.bind(this), this.DEBOUNCE_MS);
    this.input.addEventListener('input', this.debouncedSearch, { signal });
    this.closeBtn.addEventListener('click', () => this.clearInput(), { signal });

    // Enter key
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const query = this.input.value.trim();
        if (query.length > 0) {
          this.addRecentSearch(query);
          const searchUrl = this.translations.searchUrl || '/search';
          window.location.href = `${searchUrl}?q=${encodeURIComponent(query)}`;
        }
      }
    }, { signal });

    // Clear recent buttons
    this.querySelectorAll('[data-pso-clear-recent]').forEach((btn) => {
      btn.addEventListener('click', () => this.clearRecentSearches(), { signal });
    });

    // Suggestion link
    if (this.suggestionLink) {
      this.suggestionLink.addEventListener('click', (e) => {
        e.preventDefault();
        const term = this.suggestionLink.textContent.trim();
        this.input.value = term;
        this.onInput();
      }, { signal });
    }

    // Scroll arrows
    this.initScrollArrows();
  }

  /* ---- Trigger binding (runs on page load) ---- */
  bindTriggers() {
    const triggers = document.querySelectorAll('[data-predictive-overlay="true"]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.triggerElement = trigger;
        this.show();
      }, { capture: true });
    });
  }

  /* ---- Open / Close ---- */
  get open() {
    return this.hasAttribute('open');
  }

  show() {
    if (this.open) return;

    // Lazy init on first open
    if (!this._initialized) {
      this._initOverlay();
      this._initialized = true;
    }

    this.hidden = false;
    this.setAttribute('open', '');
    document.body.classList.add('pso-open');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setAttribute('active', '');
        this.input.focus();
      });
    });

    this.showDefaultState();
    this.renderRecentSearches();
  }

  hide() {
    if (!this.open) return;
    this.removeAttribute('active');

    const onEnd = () => {
      this.removeAttribute('open');
      this.hidden = true;
      document.body.classList.remove('pso-open');
      this.input.value = '';
      this.closeBtn.hidden = true;
      if (this.triggerElement) {
        this.triggerElement.focus();
        this.triggerElement = null;
      }
    };

    this.container.addEventListener('transitionend', onEnd, { once: true });
    setTimeout(onEnd, 400);
  }

  /* ---- Panel switching ---- */
  showDefaultState() {
    this.setPanel('default');
  }

  setPanel(name) {
    ['default', 'results', 'no-results', 'loading'].forEach((p) => {
      const el = this.querySelector(`[data-pso-panel="${p}"]`);
      if (el) el.hidden = p !== name;
    });
  }

  /* ---- Input handling ---- */
  onInput() {
    const query = this.input.value.trim();
    this.closeBtn.hidden = query.length === 0;

    if (query.length < this.MIN_CHARS) {
      this.showDefaultState();
      this.renderRecentSearches();
      return;
    }

    this.fetchResults(query);
  }

  clearInput() {
    this.input.value = '';
    this.closeBtn.hidden = true;
    this.showDefaultState();
    this.renderRecentSearches();
    this.input.focus();
  }

  /* ---- Fetch via section rendering (uses card-product snippet) ---- */
  fetchResults(query) {
    const cacheKey = query.toLowerCase();
    if (this.cachedResults.has(cacheKey)) {
      this.processRenderedResults(query, this.cachedResults.get(cacheKey));
      return;
    }

    this.setPanel('loading');

    if (this.fetchController) this.fetchController.abort();
    this.fetchController = new AbortController();

    const url = new URL(this.searchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', this.maxResults);
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('section_id', 'predictive-search-overlay-results');

    fetch(url.toString(), { signal: this.fetchController.signal })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.text();
      })
      .then((html) => {
        this.cachedResults.set(cacheKey, html);
        this.processRenderedResults(query, html);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        this.showDefaultState();
      });
  }

  processRenderedResults(query, html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const countsEl = doc.querySelector('[data-pso-result-counts]');
    if (!countsEl) {
      this.renderNoResults(query);
      return;
    }

    let counts;
    try { counts = JSON.parse(countsEl.textContent); } catch (e) {
      this.renderNoResults(query);
      return;
    }

    const totalResults = (counts.products || 0) + (counts.collections || 0) + (counts.pages || 0);
    if (totalResults === 0) {
      this.renderNoResults(query);
      return;
    }

    this.setPanel('results');

    // Recent searches in results
    if (this.showRecentInResults && this.getRecentSearches().length > 0) {
      this.renderRecentPills(this.recentResults);
      this.recentResults.hidden = false;
    } else {
      this.recentResults.hidden = true;
    }

    // Collections
    const renderedCollections = doc.querySelector('[data-pso-rendered-collections]');
    if (counts.collections > 0 && renderedCollections) {
      this.collectionsList.innerHTML = renderedCollections.innerHTML;
      this.collectionsSection.hidden = false;
      this.collectionsList.querySelectorAll('a').forEach((a) => {
        a.addEventListener('click', () => {
          const q = this.input.value.trim();
          if (q) this.addRecentSearch(q);
        }, { signal: this.abortController.signal });
      });
    } else {
      this.collectionsSection.hidden = true;
    }

    // Blog articles
    const renderedPages = doc.querySelector('[data-pso-rendered-pages]');
    if (counts.pages > 0 && renderedPages) {
      this.pagesList.innerHTML = renderedPages.innerHTML;
      this.pagesSection.hidden = false;
      this.pagesList.querySelectorAll('a').forEach((a) => {
        a.addEventListener('click', () => {
          const q = this.input.value.trim();
          if (q) this.addRecentSearch(q);
        }, { signal: this.abortController.signal });
      });
    } else {
      this.pagesSection.hidden = true;
    }

    // Products
    const renderedProducts = doc.querySelector('[data-pso-rendered-products]');
    if (counts.products > 0 && renderedProducts) {
      this.productsLabel.textContent = `${this.translations.products || 'PRODUCTOS'} (${counts.products})`.toUpperCase();
      this.productsRow.innerHTML = renderedProducts.innerHTML;
      this.productsSection.hidden = false;
      this.updateScrollArrows(this.productsRow.closest('.pso__scroll-wrapper'));
      this.productsRow.querySelectorAll('a').forEach((a) => {
        a.addEventListener('click', () => {
          const q = this.input.value.trim();
          if (q) this.addRecentSearch(q);
        }, { signal: this.abortController.signal });
      });
    } else {
      this.productsSection.hidden = true;
    }

    // View all link
    const searchUrl = this.translations.searchUrl || '/search';
    this.viewAllLink.href = `${searchUrl}?q=${encodeURIComponent(query)}`;
    this.viewAllLink.addEventListener('click', () => {
      const q = this.input.value.trim();
      if (q) this.addRecentSearch(q);
    }, { once: true, signal: this.abortController.signal });
  }

  /* ---- No results ---- */
  renderNoResults(query) {
    this.setPanel('no-results');
    const headingTemplate = this.translations.noResultsHtml || 'No hay resultados para "{{ terms }}"';
    this.noResultsHeading.innerHTML = headingTemplate.replace('{{ terms }}', this.escHtml(query));
    this.suggestionEl.hidden = true;
  }

  /* ---- Recent searches ---- */
  getRecentSearches() {
    try {
      const data = localStorage.getItem(this.RECENT_KEY);
      return data ? JSON.parse(data) : [];
    } catch { return []; }
  }

  addRecentSearch(term) {
    const normalized = term.trim().toLowerCase();
    if (!normalized) return;
    let recent = this.getRecentSearches().filter((t) => t.toLowerCase() !== normalized);
    recent.unshift(term.trim());
    if (recent.length > this.MAX_RECENT) recent = recent.slice(0, this.MAX_RECENT);
    try { localStorage.setItem(this.RECENT_KEY, JSON.stringify(recent)); } catch { /* noop */ }
  }

  clearRecentSearches() {
    try { localStorage.removeItem(this.RECENT_KEY); } catch { /* noop */ }
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    this.renderRecentPills(this.recentDefault);
    if (this.showRecentInResults) {
      this.renderRecentPills(this.recentResults);
    }
  }

  renderRecentPills(container) {
    if (!container) return;
    const recent = this.getRecentSearches();
    const pillsEl = container.querySelector('[data-pso-recent-pills]');
    if (!pillsEl) return;

    if (recent.length === 0) {
      container.hidden = true;
      return;
    }

    pillsEl.innerHTML = recent.map((term) =>
      `<button type="button" class="pso__pill" data-pso-recent-term="${this.escAttr(term)}">${this.escHtml(term)}</button>`
    ).join('');

    pillsEl.querySelectorAll('.pso__pill').forEach((pill) => {
      pill.addEventListener('click', () => {
        this.input.value = pill.dataset.psoRecentTerm;
        this.closeBtn.hidden = false;
        this.onInput();
      }, { signal: this.abortController.signal });
    });

    container.hidden = false;
  }

  /* ---- Scroll arrows ---- */
  initScrollArrows() {
    this.querySelectorAll('.pso__scroll-wrapper').forEach((wrapper) => {
      const row = wrapper.querySelector('[data-pso-scroll]');
      const leftArrow = wrapper.querySelector('.pso__arrow--left');
      const rightArrow = wrapper.querySelector('.pso__arrow--right');
      if (!row || !leftArrow || !rightArrow) return;

      const update = () => this.updateScrollArrows(wrapper);

      row.addEventListener('scroll', update, { passive: true, signal: this.abortController.signal });
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(update);
        ro.observe(row);
      }

      leftArrow.addEventListener('click', () => {
        const card = row.querySelector('.pso__rendered-card, .product-card');
        const gap = parseFloat(getComputedStyle(row).gap) || 0;
        const step = card ? card.offsetWidth + gap : row.clientWidth * 0.8;
        row.scrollBy({ left: -step, behavior: 'smooth' });
      }, { signal: this.abortController.signal });

      rightArrow.addEventListener('click', () => {
        const card = row.querySelector('.pso__rendered-card, .product-card');
        const gap = parseFloat(getComputedStyle(row).gap) || 0;
        const step = card ? card.offsetWidth + gap : row.clientWidth * 0.8;
        row.scrollBy({ left: step, behavior: 'smooth' });
      }, { signal: this.abortController.signal });

      update();
    });
  }

  updateScrollArrows(wrapper) {
    if (!wrapper) return;
    const row = wrapper.querySelector('[data-pso-scroll]');
    const leftArrow = wrapper.querySelector('.pso__arrow--left');
    const rightArrow = wrapper.querySelector('.pso__arrow--right');
    if (!row || !leftArrow || !rightArrow) return;

    const scrollLeft = Math.round(row.scrollLeft);
    const maxScroll = row.scrollWidth - row.clientWidth;

    leftArrow.hidden = scrollLeft <= 1;
    rightArrow.hidden = scrollLeft >= maxScroll - 1;
  }

  /* ---- Utility methods ---- */
  debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  escHtml(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  escAttr(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

customElements.define('predictive-search-overlay', PredictiveSearchOverlay);
